import axios from 'axios'
import { ElMessageBox } from 'element-plus'

// 会话过期处理去重 Promise：并发 401 共享同一次弹窗+跳转，避免重复弹窗；
// 同时取代旧的 handling 布尔锁——旧锁在 ElMessageBox Promise 未 resolve 时会永久卡 true，
// 导致后续会话过期事件被静默吞掉。改用 Promise 后，跳转触发页面刷新，模块状态自动重置。
let expiryPromise = null
let refreshPromise = null
let refreshTimer = null
let schedulerStarted = false
let sessionRevision = 0

export const ACCESS_TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000
const REFRESH_RETRY_MS = 60 * 1000

const SESSION_EXPIRED_MESSAGE_PATTERNS = [
  'Access Denied: No Token Provided',
  'Invalid Token',
  '用户信息无效，请重新登录',
  '登录状态已失效',
  '登录已过期',
  'token已过期',
  'token失效'
]

export function clearSession() {
  // 使清理前已经发出的刷新请求失效，避免用户主动退出后被迟到的响应重新写回登录态。
  sessionRevision += 1
  cancelScheduledRefresh()
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('refreshToken')
  delete axios.defaults.headers.common.Authorization
  delete axios.defaults.headers.common.authorization
}

/**
 * 本地解析 JWT 的 exp，仅用于决定何时续期；签名与权限仍由后端校验。
 */
export function getAccessTokenExp(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = payload.length % 4
    if (pad) payload += '='.repeat(4 - pad)
    const decoded = JSON.parse(decodeURIComponent(escape(atob(payload))))
    const exp = Number(decoded?.exp)
    return Number.isFinite(exp) ? exp : null
  } catch (_) {
    return null
  }
}

function cancelScheduledRefresh() {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

function scheduleAccessTokenRefresh(
  token = localStorage.getItem('token'),
  minimumDelayMs = 0
) {
  cancelScheduledRefresh()

  if (!token || !hasRefreshToken()) return
  const exp = getAccessTokenExp(token)
  if (exp === null) return

  const delay = Math.max(
    Math.max(0, minimumDelayMs),
    exp * 1000 - Date.now() - ACCESS_TOKEN_REFRESH_LEAD_MS
  )
  refreshTimer = setTimeout(async () => {
    refreshTimer = null
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return
    }
    const refreshed = await refreshAccessToken()
    if (refreshed?.token) return

    // 网络瞬时失败时，在旧 Access Token 真正过期前做有限重试；
    // 已过期或 refreshToken 无效时交由下一次请求统一触发登录失效流程。
    const currentToken = localStorage.getItem('token')
    const currentExp = getAccessTokenExp(currentToken)
    if (currentExp !== null && currentExp * 1000 > Date.now()) {
      refreshTimer = setTimeout(() => {
        refreshTimer = null
        void ensureFreshAccessToken()
      }, REFRESH_RETRY_MS)
    }
  }, delay)
}

export function persistSessionTokens({ token, refreshToken }) {
  if (!token) return null

  localStorage.setItem('token', token)
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  }
  axios.defaults.headers.common.Authorization = `Bearer ${token}`
  scheduleAccessTokenRefresh(token)

  return { token, refreshToken: refreshToken || localStorage.getItem('refreshToken') || '' }
}

export function hasRefreshToken() {
  return Boolean(localStorage.getItem('refreshToken'))
}

export async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    return null
  }

  if (!refreshPromise) {
    const requestRevision = sessionRevision
    refreshPromise = axios.post('/api/auth/refresh', { refreshToken }, {
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' }
    })
      .then((response) => {
        if (requestRevision !== sessionRevision) {
          return null
        }
        const payload = response?.data || {}
        const data = payload.data || {}
        if (!payload.success || !data.token) {
          return null
        }
        return persistSessionTokens(data)
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

/**
 * 返回一个可用于请求的 Access Token。Token 缺失、已过期或即将过期时，
 * 优先使用 refreshToken 静默续期；并发调用共享 refreshPromise。
 */
export async function ensureFreshAccessToken(options = {}) {
  const minValidityMs = Number.isFinite(options.minValidityMs)
    ? Math.max(0, options.minValidityMs)
    : ACCESS_TOKEN_REFRESH_LEAD_MS
  const nowMs = Number.isFinite(options.nowMs) ? options.nowMs : Date.now()
  const token = localStorage.getItem('token') || ''
  const exp = getAccessTokenExp(token)

  if (token && (exp === null || exp * 1000 - nowMs > minValidityMs)) {
    scheduleAccessTokenRefresh(token)
    return token
  }

  const refreshed = await refreshAccessToken()
  if (refreshed?.token) {
    return refreshed.token
  }

  // 提前续期遭遇临时网络问题时，仍允许使用尚未真正过期的旧 Token。
  const currentToken = localStorage.getItem('token') || token
  const currentExp = getAccessTokenExp(currentToken)
  if (currentToken && (currentExp === null || currentExp * 1000 > nowMs)) {
    scheduleAccessTokenRefresh(currentToken, REFRESH_RETRY_MS)
    return currentToken
  }

  return null
}

/**
 * 页面常驻时按 exp 定时续期；浏览器从后台恢复时再做一次即时检查，
 * 避免后台标签页定时器被节流后带着过期 Token 继续操作。
 */
export function startSessionRefreshScheduler() {
  if (schedulerStarted || typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  schedulerStarted = true
  scheduleAccessTokenRefresh()

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('focus', handlePageActive)
  }
  if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', handlePageActive)
  }
}

export function stopSessionRefreshScheduler() {
  cancelScheduledRefresh()
  if (!schedulerStarted || typeof window === 'undefined' || typeof document === 'undefined') {
    schedulerStarted = false
    return
  }

  if (typeof window.removeEventListener === 'function') {
    window.removeEventListener('focus', handlePageActive)
  }
  if (typeof document.removeEventListener === 'function') {
    document.removeEventListener('visibilitychange', handlePageActive)
  }
  schedulerStarted = false
}

function handlePageActive() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  void ensureFreshAccessToken()
}

export function buildLoginUrl(currentPath = '') {
  const normalizedPath = String(currentPath || '')
  const isMobilePath = normalizedPath === '/mobile' || normalizedPath.startsWith('/mobile/')
  const loginPath = isMobilePath ? '/mobile/login' : '/login'

  if (!normalizedPath || normalizedPath === loginPath) {
    return loginPath
  }

  return `${loginPath}?redirect=${encodeURIComponent(normalizedPath)}`
}

function redirectToLogin() {
  const currentPath = window.location.pathname + window.location.search + window.location.hash
  const loginUrl = buildLoginUrl(currentPath)
  window.location.href = loginUrl
}

export async function handleSessionExpired(options = {}) {
  // 并发去重：若已有一次过期处理在进行，复用同一个 Promise，避免并发 401 重复弹窗/跳转
  if (expiryPromise) return expiryPromise

  expiryPromise = (async () => {
    try {
      const { message = '登录状态已失效，请重新登录。', redirect = true } = options

      // 清除会话数据
      clearSession()

      // 显示提示弹窗
      await ElMessageBox.alert(message, '需要重新登录', {
        type: 'warning',
        confirmButtonText: '重新登录'
      })

      // 重定向到登录页
      if (redirect) {
        redirectToLogin()
      }
    } finally {
      expiryPromise = null
    }
  })()

  return expiryPromise
}

export function resetSessionHandlingState() {
  expiryPromise = null
}

export function isSessionExpiryMessage(message) {
  const text = String(message || '').trim()
  if (!text) {
    return false
  }
  return SESSION_EXPIRED_MESSAGE_PATTERNS.some((pattern) => text.includes(pattern))
}

export function isSessionExpiryResponse(error) {
  const status = Number(error?.response?.status || 0)
  if (status !== 401) {
    return false
  }

  const data = error?.response?.data || {}
  const message = String(data.message || data.error || '').trim()
  if (!message) {
    return false
  }

  return isSessionExpiryMessage(message)
}
